import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import CardService, { CardProps } from 'services/hooks/card-service';
import OrganizationService, { OrganizationProps } from 'services/hooks/organization-service';
import { FilterSelect } from 'utils/FilterSelectUtils';
import { Table } from '../Common/Table';
import Save from './CardForms/save';
import Delete from './CardForms/delete';
import { useAuth } from 'services/auth-service';
import { CardFormat } from 'utils/constants';
import { generateApiFailErrorMessage } from 'utils/helpers';
import { toast } from 'react-toastify';
import FullScreenLoader from 'components/elements/Spinner/FullScreenLoader';

function CardSaveMode() {
  const [getAllCardsResult, setgetAllCardsResult] = useState<CardProps[] | null>([]);
  const [orgList, setOrgList] = useState<OrganizationProps[] | null>();
  const {
    state: { user }
  } = useAuth();

  const {
    isLoading,
    refetch,
    data: cardsData
  } = useQuery<CardProps[], Error>('cards', () => CardService.findAllCards(), {
    onError: () => {
      toast.error(generateApiFailErrorMessage('Cards'));
    }
  });

  const { data: OrgData } = useQuery<OrganizationProps[], Error>(
    'organizations',
    OrganizationService.findAllOrgs,
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage('Organizations'));
      }
    }
  );

  useEffect(() => {
    if (cardsData && Array.isArray(cardsData)) {
      setgetAllCardsResult(cardsData);
    }
    if (OrgData && Array.isArray(OrgData)) {
      setOrgList(OrgData);
    }
  }, [OrgData, cardsData]);

  const columns = useMemo(
    () => [
      {
        id: 'organization',
        Header: 'Organization',
        accessor: (row: { organizationUuid: string }) => {
          return orgList?.find((f: { uuid: string }) => f?.uuid === row?.organizationUuid)?.name;
        },
        Filter: FilterSelect,
        disableSortBy: false
      },
      {
        id: 'description',
        Header: 'Description',
        accessor: 'description',
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'badgeId',
        Header: 'Badge Id',
        accessor: 'badgeId',
        Cell: (cell: { value: string | number }) => (
          <div className="rounded-md ring ring-neutral3 text-xs uppercase inline-block p-2">
            {cell?.value}
          </div>
        ),
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'facilityCode',
        Header: 'Facility Code',
        accessor: 'facilityCode',
        Cell: (cell: { value: string | number }) => (
          <div className="rounded-md ring ring-neutral3 text-xs uppercase inline-block p-2">
            {cell?.value}
          </div>
        ),
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'status',
        Header: 'Status',
        accessor: 'status',
        Cell: (cell: { value: string }) =>
          cell?.value === 'UNASSIGNED' ? (
            <div className="flex">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-circle-fill mt-1.5"
                viewBox="0 0 16 16"
              >
                <circle cx="5" cy="5" r="5" fill="#FF0000" />
              </svg>
              {cell?.value}
            </div>
          ) : (
            <div className="flex">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-circle-fill mt-1.5"
                viewBox="0 0 16 16"
              >
                <circle cx="5" cy="5" r="5" fill="#008000" />
              </svg>
              {cell?.value}
            </div>
          ),
        Filter: FilterSelect,
        filter: 'equals'
      },
      {
        id: 'assigned/issued',
        Header: 'Assigned/Issued To',
        accessor: (row: { mobileUser: { firstName: string; lastName: string } }) => {
          return `${row?.mobileUser?.firstName} ${row?.mobileUser?.lastName}`;
        },
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'actions',
        Header: 'Actions',
        accessor: (data: any) => data,
        Cell: (row: any) => {
          return (
            <div className="flex gap-4">
              {row.value.format !== CardFormat.AVL && (
                <SecondaryButton
                  onClick={() => {
                    row.setEditUUID(row.value.uuid);
                    row.setUpdateShowModal(true);
                  }}
                  buttonLabel="Edit"
                  variant="default"
                  size="px-4 w-auto"
                />
              )}
              {(user?.role === 'SUPER' || user?.role === 'ACCOUNT') && (
                <SecondaryButton
                  onClick={() => {
                    row.setDeleteUUID(row.value.uuid);
                    row.setDeleteShowModal(true);
                  }}
                  buttonLabel="Delete"
                  variant="default"
                  size="px-4 w-auto"
                />
              )}
            </div>
          );
        },
        width: 150,
        disableFilters: true
      }
    ],
    [orgList, user?.role]
  );

  if (isLoading || !getAllCardsResult) {
    return <FullScreenLoader />;
  }

  return (
    <div className="cards">
      <Table
        name="Cards"
        refetch={refetch}
        columns={columns}
        data={getAllCardsResult}
        defaultSortBy={{ id: 'organization', desc: false }}
        CreateForm={Save}
        DeleteForm={Delete}
        createQuery={CardService.createCard}
        updateQuery={CardService.updateCard}
        deleteQuery={CardService.deleteCard}
        editQuery={CardService.findCardById}
        queryName="cards"
        buttonName="Card"
        toolTipMessage="Create and manage digital access cards for occupants."
      />
    </div>
  );
}

export default CardSaveMode;
